import React, { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Page from '../Page';
import { useAuth } from '../../hooks/use-auth';
import { apiUrl } from '../../config';

const dateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

function Detail({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography component="div" sx={{ overflowWrap: 'anywhere' }}>
        {children ?? '—'}
      </Typography>
    </Box>
  );
}

function BackendRecords({ title, records }) {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      {!records.length ? (
        <Typography color="textSecondary">
          No matching backend records.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label={title}>
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Original transaction ID</TableCell>
                <TableCell>Backend expiry</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>
                    {record.userId ? (
                      <Link
                        component={RouterLink}
                        to={`/trainer/users/${record.userId}/subscription`}
                      >
                        {record.userId}
                      </Link>
                    ) : (
                      'Unassigned'
                    )}
                  </TableCell>
                  <TableCell>{record.originalTransactionId}</TableCell>
                  <TableCell>{dateLabel(record.expiresAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default function SubscriptionTools() {
  const auth = useAuth();
  const [orderId, setOrderId] = useState('');
  const [trainerId, setTrainerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const inFlight = useRef(false);

  async function lookup(event) {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const token = await auth.user.getIdToken();
      const response = await fetch(
        `${apiUrl}/v1/subscriptions/apple/order-lookup`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderId: orderId.trim().toUpperCase(),
            ...(trainerId.trim() ? { trainerId: trainerId.trim() } : {}),
          }),
        }
      );
      const data = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          data?.message ||
            'Unable to check the order with Apple. Please try again.'
        );
      if (
        !data ||
        !Array.isArray(data.transactions) ||
        !Array.isArray(data.subscriptions)
      ) {
        throw new Error(
          'Unable to read Apple’s lookup result. Please try again.'
        );
      }
      setResult(data);
    } catch (failure) {
      setError(
        failure.message ||
          'Unable to check the order with Apple. Please try again.'
      );
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }

  return (
    <Page title="Subscription Tools">
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" gutterBottom>
              Subscription Tools
            </Typography>
            <Typography color="textSecondary">
              Check a customer’s Apple purchase and current subscription status.
            </Typography>
          </Box>
          <Card>
            <CardHeader title="Look up an Apple Order ID" />
            <Divider />
            <CardContent>
              <Stack component="form" onSubmit={lookup} spacing={3}>
                <TextField
                  label="Apple Order ID"
                  value={orderId}
                  required
                  disabled={loading}
                  onChange={(event) => {
                    setOrderId(event.target.value);
                    setResult(null);
                    setError('');
                  }}
                  helperText="Use the Order ID from the customer’s Apple email receipt, not a transaction ID."
                  inputProps={{ maxLength: 64, pattern: '[A-Za-z0-9]{6,64}' }}
                />
                <TextField
                  label="Trainer ID (optional)"
                  value={trainerId}
                  disabled={loading}
                  onChange={(event) => {
                    setTrainerId(event.target.value);
                    setResult(null);
                    setError('');
                  }}
                  helperText="Leave blank for your own app. Admins can enter another trainer’s ID."
                  inputProps={{ maxLength: 24, pattern: '[a-fA-F0-9]{24}' }}
                />
                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading || !orderId.trim()}
                  >
                    {loading ? 'Checking Apple…' : 'Check with Apple'}
                  </Button>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
              </Stack>
            </CardContent>
          </Card>
          {result && (
            <>
              <Alert severity="info">
                Checked with Apple at {dateLabel(result.checkedAt)} for{' '}
                {result.bundleId} ({result.environment}). This lookup does not
                change backend subscriptions. A past purchase alone does not
                mean the subscription is still active.
              </Alert>
              <Card>
                <CardHeader
                  title="Current Apple subscriptions"
                  subheader="Current status across this customer’s subscription groups for this app."
                />
                <Divider />
                <CardContent>
                  {!result.subscriptions.length ? (
                    <Typography>
                      No auto-renewable subscriptions were found in this order.
                    </Typography>
                  ) : (
                    <Stack spacing={3}>
                      {result.subscriptions.map((subscription) => (
                        <Box key={subscription.originalTransactionId}>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{ mb: 2 }}
                          >
                            <Typography variant="h5">
                              {subscription.productId}
                            </Typography>
                            <Chip
                              label={subscription.statusLabel}
                              color={
                                subscription.status === 1
                                  ? 'success'
                                  : subscription.status === 4
                                  ? 'warning'
                                  : 'default'
                              }
                            />
                          </Stack>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={4}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            <Detail label="Original transaction ID">
                              {subscription.originalTransactionId}
                            </Detail>
                            <Detail label="Expires">
                              {dateLabel(subscription.expiresDate)}
                            </Detail>
                            <Detail label="Auto-renew">
                              {subscription.autoRenewStatus === 1
                                ? 'On'
                                : subscription.autoRenewStatus === 0
                                ? 'Off'
                                : 'Unknown'}
                            </Detail>
                            <Detail label="Ownership">
                              {subscription.ownershipType}
                            </Detail>
                            {subscription.gracePeriodExpiresDate && (
                              <Detail label="Grace period ends">
                                {dateLabel(subscription.gracePeriodExpiresDate)}
                              </Detail>
                            )}
                            {subscription.revocationDate && (
                              <Detail label="Revoked">
                                {dateLabel(subscription.revocationDate)}
                              </Detail>
                            )}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader title={`Purchases in order ${result.orderId}`} />
                <Divider />
                <CardContent>
                  <Stack spacing={3}>
                    {result.transactions.map((transaction) => (
                      <Stack
                        key={transaction.transactionId}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={4}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <Detail label="Product">{transaction.productId}</Detail>
                        <Detail label="Transaction ID">
                          {transaction.transactionId}
                        </Detail>
                        <Detail label="Purchased">
                          {dateLabel(transaction.purchaseDate)}
                        </Detail>
                        <Detail label="Expiry on this purchase">
                          {dateLabel(transaction.expiresDate)}
                        </Detail>
                        {transaction.revocationDate && (
                          <Detail label="Revoked">
                            {dateLabel(transaction.revocationDate)}
                          </Detail>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card>
                <CardHeader
                  title="Backend account matches"
                  subheader="Compare Apple’s current status with the subscription records linked to these transaction IDs."
                />
                <Divider />
                <CardContent>
                  <Stack spacing={3}>
                    <BackendRecords
                      title="Subscriptions"
                      records={result.backendSubscriptions || []}
                    />
                    <BackendRecords
                      title="Subscription orders"
                      records={result.backendOrders || []}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}
        </Stack>
      </Container>
    </Page>
  );
}
